/* ═══════════════════════════════════════════════════════════════
   useDesignerCollab - the Designer's live shared document
   (docs/SPEC_WORKFLOW.md §5). One Yjs doc per project spec
   (`project:<id>`), persisted on the project_specs row by the collab
   server (migration 059). The Designer's own 5s-debounced save stays the
   JSON mirror every other reader uses - this hook only owns the socket,
   the one-time seeding (the exact helpers the Spec Studio uses -
   collabSeed.ts / collabState.ts) and the bind-after-sync rule.

   Read-only until bound: the paper is held (`pending`) from the moment a
   shared document is expected until it has synced AND been seeded, so a
   keystroke can never land in a document that is about to be replaced.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type * as Y from 'yjs';
import type { ContentBlock } from '../../shared/types';
import { useCollabDoc } from '../spec-studio/useCollabDoc';
import { COLLAB_FIELD, type SpecCanvasCollab } from '../spec-studio/collabBinding';
import { isDocSeeded, readSeeder, seedDoc } from '../spec-studio/collabSeed';
import {
  collabDocName,
  shouldSeed,
  type CollabPeer,
  type CollabStatus,
} from '../spec-studio/collabState';

export interface DesignerCollab {
  /** Present once synced + seeded; stays bound through a later disconnect
   *  (offline typing queues in the Y.Doc and merges on reconnect). */
  collab: SpecCanvasCollab | null;
  /** A shared document is wired for this spec (token present, not refused,
   *  not discarded) - the chip then reads the collab status. */
  shared: boolean;
  /** Shared but not yet bound: hold the paper read-only. */
  pending: boolean;
  status: CollabStatus;
  unsyncedChanges: number;
  peers: CollabPeer[];
  /** The row's content changed outside the editor: the socket is closed. */
  reset: boolean;
}
/**
 * @param docId   the project spec id once its row is loaded, else null (idle).
 * @param canEdit `project_specs.edit_*` - a viewer never seeds and the
 *                server opens the socket read-only for them.
 * @param getSeedBlocks the row's `coreContent`, read only at seed time.
 */
export function useDesignerCollab(o: {
  docId: string | null;
  userName: string;
  canEdit: boolean;
  getSeedBlocks: () => ContentBlock[] | null;
}): DesignerCollab {
  const { docId, userName, canEdit } = o;
  const seedBlocksRef = React.useRef(o.getSeedBlocks);
  seedBlocksRef.current = o.getSeedBlocks;

  const docName = docId ? collabDocName('project', docId) : null;
  const collabDoc = useCollabDoc(docName, userName);
  const ydoc: Y.Doc | null = collabDoc?.ydoc ?? null;
  const provider = collabDoc?.provider ?? null;
  const synced = !!collabDoc?.synced;
  const seat = collabDoc?.seat ?? '';

  // Per-document state is keyed to the Y.Doc itself, so a new section's
  // document never inherits the last one's flags.
  const [seededDoc, setSeededDoc] = React.useState<Y.Doc | null>(null);
  const [seederOf, setSeederOf] = React.useState<{ doc: Y.Doc | null; seat: string | null }>({
    doc: null,
    seat: null,
  });
  const [boundDoc, setBoundDoc] = React.useState<Y.Doc | null>(null);
  const seeded = ydoc !== null && seededDoc === ydoc;
  const seeder = seederOf.doc === ydoc ? seederOf.seat : null;

  React.useEffect(() => {
    if (!ydoc) return;
    const meta = ydoc.getMap<unknown>('meta');
    const fragment = ydoc.getXmlFragment(COLLAB_FIELD);
    const read = () => {
      setSeederOf({ doc: ydoc, seat: readSeeder(ydoc) });
      if (isDocSeeded(ydoc)) setSeededDoc(ydoc);
    };
    meta.observe(read);
    fragment.observeDeep(read);
    read();
    return () => {
      meta.unobserve(read);
      fragment.unobserveDeep(read);
    };
  }, [ydoc]);

  // Seed (exactly one writer, once).
  React.useEffect(() => {
    if (!ydoc || !synced || seeded) return;
    if (isDocSeeded(ydoc)) {
      setSeededDoc(ydoc);
      return;
    }
    if (
      !shouldSeed({
        synced,
        readOnly: !canEdit,
        fragmentEmpty: ydoc.getXmlFragment(COLLAB_FIELD).length === 0,
        seeded: false,
        seeder,
        mySeat: seat,
      })
    ) {
      return;
    }
    const blocks = seedBlocksRef.current();
    if (!blocks) return;
    seedDoc(ydoc, blocks);
    setSeededDoc(ydoc);
  }, [ydoc, synced, seeded, seeder, seat, canEdit]);

  React.useEffect(() => {
    if (ydoc && seeded && synced) setBoundDoc(ydoc);
  }, [ydoc, seeded, synced]);

  const discarded = !!collabDoc?.discarded;
  const denied = !!collabDoc?.denied;
  const boundUser = collabDoc?.user ?? null;
  const collab = React.useMemo<SpecCanvasCollab | null>(
    () =>
      ydoc && provider && boundUser && boundDoc === ydoc && !discarded
        ? { ydoc, provider, user: boundUser }
        : null,
    [ydoc, provider, boundUser, boundDoc, discarded]
  );
  const shared = !!collabDoc && !denied && !discarded;

  return {
    collab,
    shared,
    pending: shared && !collab,
    status: collabDoc?.status ?? 'connecting',
    unsyncedChanges: collabDoc?.unsyncedChanges ?? 0,
    peers: collabDoc?.peers ?? [],
    reset: !!collabDoc?.reset,
  };
}
